/**
 * Persist verse scores to Firestore so rabbis can see students' best scores.
 * Document ID: sanitized userId_verseKey for upsert.
 */
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  getFirestore,
  doc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

const COLLECTION = 'verseScores';

export interface VerseScoreDoc {
  userId: string;
  verseKey: string;
  parasha: string;
  verseIndex: number;
  bestScore: number;
  lastUpdated: ReturnType<typeof Timestamp.now>;
}

function sanitizeId(s: string): string {
  return s.replace(/[./#[\]@]/g, '_');
}

/**
 * Save or update best score for a verse. Only updates if new score is higher.
 */
export async function saveVerseScoreToFirestore(
  userId: string,
  verseKey: string,
  parasha: string,
  verseIndex: number,
  newScore: number,
): Promise<void> {
  if (!userId || !verseKey) return;
  const docId = `${sanitizeId(userId)}_${sanitizeId(verseKey)}`;
  const ref = doc(db, COLLECTION, docId);
  const existing = await getDoc(ref);
  const existingData = existing.exists() ? existing.data() : null;
  const currentBest = (existingData?.bestScore as number) ?? 0;
  if (newScore <= currentBest) return;
  await setDoc(
    ref,
    {
      userId,
      verseKey,
      parasha,
      verseIndex: Number(verseIndex),
      bestScore: newScore,
      lastUpdated: Timestamp.now(),
    },
    { merge: true },
  );
}

/**
 * Fetch all verse scores for a student (for rabbi's student details page).
 */
export async function getVerseScoresByUser(userId: string): Promise<VerseScoreDoc[]> {
  if (!userId) return [];
  const q = query(collection(db, COLLECTION), where('userId', '==', userId));
  const snapshot = await getDocs(q);
  const list: VerseScoreDoc[] = [];
  snapshot.forEach((d) => {
    const data = d.data();
    list.push({
      userId: data.userId,
      verseKey: data.verseKey,
      parasha: data.parasha,
      verseIndex: data.verseIndex ?? 0,
      bestScore: data.bestScore ?? 0,
      lastUpdated: data.lastUpdated,
    });
  });
  list.sort((a, b) => {
    const parashaCmp = (a.parasha || '').localeCompare(b.parasha || '');
    if (parashaCmp !== 0) return parashaCmp;
    return (a.verseIndex ?? 0) - (b.verseIndex ?? 0);
  });
  return list;
}
